import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Playlist } from '../../types';
import { PlaylistType, PlaylistStatus } from '../../types';

interface PlaylistDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (playlistData: any) => Promise<void>;
  playlist?: Playlist | null;
  mode: 'create' | 'edit';
}

interface PlaylistFormData {
  name: string;
  description: string;
  is_loop: boolean;
  shuffle: boolean;
  is_public: boolean;
  status: PlaylistStatus;
  type: PlaylistType;
}

const PlaylistDialog: React.FC<PlaylistDialogProps> = ({
  open,
  onClose,
  onSave,
  playlist,
  mode,
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<PlaylistFormData>({
    name: '',
    description: '',
    is_loop: true,
    shuffle: false,
    is_public: true,
    status: PlaylistStatus.DRAFT,
    type: PlaylistType.STANDARD,
  });
  
  const [errors, setErrors] = useState<Partial<PlaylistFormData>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (playlist && mode === 'edit') {
      setFormData({
        name: playlist.name || '',
        description: playlist.description || '',
        is_loop: playlist.loop_enabled ?? true,
        shuffle: playlist.shuffle_enabled ?? false,
                  is_public: !(playlist?.is_default ?? false), // Инвертируем is_default для is_public
        status: (playlist.status as PlaylistStatus) || PlaylistStatus.DRAFT,
        type: (playlist.type as PlaylistType) || PlaylistType.STANDARD,
      });
    } else {
      // Сброс формы для создания
      setFormData({
        name: '',
        description: '',
        is_loop: true,
        shuffle: false,
        is_public: true,
        status: PlaylistStatus.DRAFT,
        type: PlaylistType.STANDARD,
      });
    }
    setErrors({});
  }, [playlist, mode, open]);

  const handleInputChange = (field: keyof PlaylistFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? (event.target as HTMLInputElement).checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Очищаем ошибку для этого поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSelectChange = (field: keyof PlaylistFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<PlaylistFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('common.required');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('common.minLength').replace('{{count}}', '2');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Ошибка сохранения плейлиста:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const title = mode === 'create' 
    ? t('playlists.createPlaylist') 
    : t('playlists.editPlaylist');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        {title}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            {/* Основная информация */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                {t('playlists.basicInfo')}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('playlists.name')}
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
                disabled={loading}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('playlists.description')}
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={3}
                disabled={loading}
                placeholder={t('playlists.descriptionPlaceholder')}
              />
            </Grid>

            {/* Настройки */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {t('playlists.settings')}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('playlists.statusLabel')}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleSelectChange('status')}
                  label={t('playlists.statusLabel')}
                  disabled={loading}
                >
                  <MenuItem value={PlaylistStatus.DRAFT}>
                    Черновик
                  </MenuItem>
                  <MenuItem value={PlaylistStatus.ACTIVE}>
                    Активный
                  </MenuItem>
                  <MenuItem value={PlaylistStatus.ARCHIVED}>
                    Архивированный
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('playlists.typeLabel')}</InputLabel>
                <Select
                  value={formData.type}
                  onChange={handleSelectChange('type')}
                  label={t('playlists.typeLabel')}
                  disabled={loading}
                >
                  <MenuItem value={PlaylistType.STANDARD}>
                    Стандартный
                  </MenuItem>
                  <MenuItem value={PlaylistType.EMERGENCY}>
                    Экстренный
                  </MenuItem>
                  <MenuItem value={PlaylistType.TEMPLATE}>
                    Шаблон
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_loop}
                      onChange={handleInputChange('is_loop')}
                      disabled={loading}
                    />
                  }
                  label={t('playlists.loop')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.shuffle}
                      onChange={handleInputChange('shuffle')}
                      disabled={loading}
                    />
                  }
                  label={t('playlists.shuffle')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_public}
                      onChange={handleInputChange('is_public')}
                      disabled={loading}
                    />
                  }
                  label={t('playlists.public')}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistDialog; 